"use client";

/**
 * The one place a `GtaFinding` becomes something a person reads.
 *
 * `findings.ts` already decided what matters and in what order; the failure this
 * component exists to prevent is the one that happens *after* that decision,
 * where eight surfaces each re-render the same ranked list in their own way and
 * quietly lose a different part of it. PackBench's panel — which this is a
 * promotion of — lost three things in exactly that way: it sliced the first
 * eight findings without sorting them, so the ranking `findings.ts` computed was
 * discarded on the way to the screen; it drew the emergency badge from an inline
 * `tags.includes` rather than from `isEmergencyFinding`, so the badge and the
 * sort could disagree about the same finding; and it never grouped, so one
 * misspelled `handlingId` read as eight unrelated errors rather than as one
 * vehicle and the eight things that stop working because of it. `GtaEntity`'s
 * type comment says grouping is the whole point of the field, and no consumer
 * was using it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULES THIS SURFACE IS HELD TO
 *
 * A count above a folded list counts every finding, not the visible slice. This
 * is the same rule `resmon.ts` and `ResmonReport.tsx` enforce for a pasted dump,
 * and it is the rule the competitor's tools break: a list showing eight rows
 * over the words "8 findings" is a report that lies about a folder with four
 * hundred problems in it. So the headline, the severity counts and the verdict
 * are computed over the whole ranked set, and only the rows are folded.
 *
 * Nothing is dropped in silence. `findings.ts` caps ranking at 20,000 findings;
 * when input exceeds that cap this says so in words rather than showing a total
 * that is 20,000 for two different folders. `report.skipped` is rendered for the
 * reason `analyze.ts`'s header gives in as many words: a report that quietly
 * drops half its rules and shows a green tick is worse than no report, and a UI
 * that throws the field away reintroduces exactly that failure at the last step.
 *
 * A refused fix is reported, never swallowed. `applyEdits` returns `applied` and
 * `rejected`, and the `expect` field on `GtaTextEdit` exists so that an edit
 * calculated against a file that has since changed can be refused instead of
 * writing garbage. A UI that renders only `applied` turns that entire mechanism
 * into a silent no-op, which is worse than having no verify step at all —
 * the user believes the fix landed. Drawing the refusal is only half of that:
 * it also has to still be there a moment later. `onApplied` exists to be a
 * callback that stores the patched text, storing it re-renders the caller, and
 * a caller doing the documented thing — `findings={sortFindings(dedupeFindings(raw))}` —
 * recomputes that expression into a new array of the same findings. Transient
 * state keyed on the identity of that array is state wiped by the re-render the
 * fix itself caused, so what the user sees is a click that did nothing. Hence
 * `findingKey`: the outcomes and the reveal are keyed on what a finding *is*.
 *
 * The fold picks the rows and grouping lays them out, in that order and not the
 * other one. Grouping first and then filling the row budget group by group
 * spends it on whatever the top group happens to contain, which is how five
 * warnings on one vehicle displace nine errors on three others while the
 * headline above them honestly reads "12 errors" — the ranking discarded on the
 * way to the screen again, one step further down.
 *
 * A finding is shown under the resource it came out of. A set report's entity
 * ids are only unique *inside* one resource — `analyze.ts` files a manifest
 * finding under `{ kind: "manifest", id: "fxmanifest.lua" }` and `vehicleMeta.ts`
 * a parse failure under the relative path, and both of those repeat in every
 * pack in the drop — so grouping a flattened set on the bare id merges two
 * vendor packs into one heading carrying two byte-identical rows. That is the
 * collapse `intake.ts` rule 2, `GtaResourceInput.identity` and `labels.ts` were
 * all written to prevent, arrived at from the last step instead of the first,
 * and it is worse than useless because the Copy Markdown button beside it
 * exports the same findings correctly attributed: the screen and its own export
 * then disagree about how many broken packs there are. So the owning resource
 * is carried through the flatten and is part of what a group is keyed on, and
 * the way a resource is *spelled* comes from `disambiguateLabels` — the same
 * rule, in the same style, that wrote the collision sentence three lines above
 * the heading.
 *
 * A count over a folded list counts findings this build understands and says so
 * when it does not understand them all. `summarizeFindings` and `verdictFor`
 * both fall through for a severity this build does not define — a real case,
 * because a finding can arrive from an older desktop build — and a panel that
 * renders such a row while counting it nowhere puts a green tick and
 * "0 errors, 0 warnings, 0 notes" over a problem it is displaying. So the
 * remainder between `ranked.length` and the three counts is reported in words
 * and the verdict is never "clean" while it is non-zero.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Limits. This renders findings; it does not judge them. Severity, ordering and
 * the emergency tag are all decided upstream, and if an analyzer marked a
 * cosmetic problem as an error this will faithfully put it first in red. It
 * cannot read or write a file — `fixes` is a callback pair for that reason, and
 * without it the fix buttons are simply not drawn. It knows nothing about which
 * tool is rendering it: the verdict wording comes from `describeVerdict`, which
 * is phrased for an export, and a tool that wants different words should say so
 * around this component rather than inside it.
 *
 * It can only attribute what it is handed. Resource attribution is read off the
 * `report` prop by finding identity, which survives `sortFindings` and
 * `dedupeFindings` because both return the same objects — but a caller that
 * copies its findings before passing them (`withTag` does) hands over objects
 * this has never seen, and those rows lose their pack name rather than being
 * given a wrong one. The sharper edge is upstream: `findingKey` is code, file,
 * line and title, none of which is qualified by resource, so a caller that runs
 * `dedupeFindings` across a whole set merges two packs' identical findings into
 * one row *before* this component sees either. Attributing the survivor to one
 * pack would be a guess, so it is left to the caller to dedupe per resource;
 * the only complete fix is for `findingKey` to learn about resources, and that
 * is `findings.ts`'s decision to make, not this file's.
 */

import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  Info,
  Siren,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { GtaResourceSetReport } from "@/lib/gtaKnowledge/analyze";
import {
  describeVerdict,
  findingKey,
  groupByEntity,
  isEmergencyFinding,
  sortFindings,
  summarizeFindings,
  verdictFor,
} from "@/lib/gtaKnowledge/findings";
import { disambiguateLabels, PROSE_LABEL_STYLE } from "@/lib/gtaKnowledge/labels";
import { normalizeName } from "@/lib/gtaKnowledge/normalize";
import { applyEdits } from "@/lib/gtaKnowledge/patch";
import { toMarkdown } from "@/lib/gtaKnowledge/report";
import type {
  GtaEntity,
  GtaEntityKind,
  GtaFinding,
  GtaFix,
  GtaResourceReport,
  GtaSeverity,
  GtaTextEdit,
} from "@/lib/gtaKnowledge/types";
import { cn } from "@/lib/utils";

// ─── Limits ──────────────────────────────────────────────────────────────────

/**
 * How many findings are drawn before the tail is folded away.
 *
 * Eight is what PackBench's panel showed and it is the right number for a panel
 * beside an export button: enough to see whether the problems are all one
 * vehicle, short enough not to push the button off screen.
 */
export const VISIBLE_FINDINGS = 8;

/**
 * How many more rows one click of the reveal mounts.
 *
 * Not "show all". `findings.ts` ranks up to 20,000 findings, and a single
 * malformed `vehicles.meta` reaches that, so a control that mounts every row at
 * once is a control that freezes the tab on precisely the input that most needs
 * reading. The same reasoning is why `ResmonReport.tsx` pages its table.
 */
export const FINDINGS_PAGE = 100;

// ─── Severity presentation ───────────────────────────────────────────────────

const SEVERITY_ICON: Record<GtaSeverity, typeof AlertCircle> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const SEVERITY_CLASS: Record<GtaSeverity, string> = {
  error: "text-destructive",
  warning: "text-warning",
  info: "text-muted-foreground",
};

/**
 * A severity an older analyzer produced and this build does not define still
 * renders. `findings.ts` ranks such a finding last rather than poisoning the
 * sort, and dropping it here would undo that care — the row would vanish and
 * the reader would never learn that the analyzer found something.
 *
 * What the row cannot do is count itself. `summarizeFindings` and `verdictFor`
 * both test for the three severities this build knows and fall through for
 * anything else, so such a finding is in `ranked.length` and in none of the
 * three counters — which is why the panel compares those two numbers and says
 * what the difference is rather than drawing a green tick over a row it is
 * showing. See `unrecognised` below.
 */
const UNKNOWN_SEVERITY_CLASS = "text-muted-foreground";

const ENTITY_LABEL: Record<GtaEntityKind, string> = {
  resource: "Resource",
  vehicle: "Vehicle",
  manifest: "Manifest",
  modkit: "Mod kit",
  siren: "Siren profile",
  light: "Light setting",
  stream_asset: "Stream asset",
  dependency: "Dependency",
};

// ─── Props ───────────────────────────────────────────────────────────────────

/**
 * A report shaped like either of the two `analyze.ts` produces.
 *
 * A single resource and a whole `resources/` folder carry their findings
 * differently — one has `vehicles`, the other has `resources` — and a component
 * that took only the first would push every set-report consumer into flattening
 * by hand, which is how two tools end up counting the same folder differently.
 */
export type FindingsReport = GtaResourceReport | GtaResourceSetReport;

/**
 * Every finding a report holds, including the ones hanging off its children.
 *
 * Exported because the alternative is each of the eight tools writing this
 * three-line flatten itself, and the version that forgets
 * `vehicles.flatMap(v => v.findings)` is the version that shows a green tick
 * over a resource with nine broken vehicles in it.
 *
 * It is one flat list and nothing more: the resource a finding came out of is
 * not in a `GtaFinding`, so a caller that needs to know must ask the report,
 * the way `attributionOf` below does. Left flat because that is what a caller
 * counting or exporting a set wants, and because widening the return type here
 * would change what eight tools receive to solve a problem only the grouped
 * rendering has.
 */
export function findingsOfReport(report: FindingsReport): GtaFinding[] {
  if ("resources" in report) {
    return [
      ...report.findings,
      ...report.resources.flatMap((resource) => findingsOfReport(resource)),
    ];
  }
  return [...report.findings, ...report.vehicles.flatMap((vehicle) => vehicle.findings)];
}

// ─── Which pack a finding is about ───────────────────────────────────────────

/**
 * The report's resources, as the panel has to talk about them.
 *
 * Two things are needed and they are not the same thing. `scopeOf` answers
 * "which resource did this finding come out of", and its value is the
 * resource's *position*, not its label, because two resources can end up
 * spelled alike — `labels.ts` says so in its LIMITS block — and a scope keyed
 * on the spelling would merge exactly the pair that is hardest to tell apart.
 * `labelOf` then answers "how is that resource spelled", once per resource.
 *
 * `resourceLabelOf` is the third question, and it is about the `resource`-kind
 * entity id specifically. `analyze.ts` sets that id to `identity || name` so
 * that this component cannot file two packs' "No manifest" findings under one
 * heading — but the identity is a drop-relative root, `vendor-a.zip/police`,
 * which appears nowhere else in the report: the collision sentence three lines
 * above the heading says `police (from vendor-a.zip)`, and a panel that prints
 * both is a panel spelling one pack two ways. So the id is resolved back to its
 * resource and the resource's label is what is drawn.
 *
 * All three come from one `disambiguateLabels` call in `PROSE_LABEL_STYLE`, the
 * same call and the same style `analyzeResourceSet` hands to `streamAssets.ts`.
 * Recomputing it rather than reading it off the report is not a second copy of
 * the rule — the rule is `labels.ts` and there is one of those — and it is what
 * lets a caller pass a report this component did not analyse.
 */
type Attribution = {
  /** Finding → the resource it was lifted out of. Empty for a single report. */
  scopeOf: ReadonlyMap<GtaFinding, string>;
  /** Scope → how that resource is spelled on screen. */
  labelOf: ReadonlyMap<string, string>;
  /** Normalised `resource`-kind entity id → how that resource is spelled. */
  resourceLabelOf: ReadonlyMap<string, string>;
};

const NO_ATTRIBUTION: Attribution = {
  scopeOf: new Map(),
  labelOf: new Map(),
  resourceLabelOf: new Map(),
};

function attributionOf(report: FindingsReport | undefined): Attribution {
  if (!report) return NO_ATTRIBUTION;
  const isSet = "resources" in report;
  const resources = isSet ? report.resources : [report];
  const labels = disambiguateLabels(resources, PROSE_LABEL_STYLE);

  const scopeOf = new Map<GtaFinding, string>();
  const labelOf = new Map<string, string>();
  const resourceLabelOf = new Map<string, string>();

  resources.forEach((resource, index) => {
    const label = labels[index];
    // Keyed the way `analyze.ts` keys the entity it writes, so the lookup is a
    // lookup and not a second guess at what the id means.
    resourceLabelOf.set(normalizeName(resource.identity || resource.name), label);
    // A single-resource report has nothing to attribute *against*: every
    // finding came out of the one resource, and a label on every row would be
    // noise. This is what keeps PackBench's panel byte-identical.
    if (!isSet) return;
    const scope = `#${index}`;
    labelOf.set(scope, label);
    for (const finding of findingsOfReport(resource)) scopeOf.set(finding, scope);
  });

  return { scopeOf, labelOf, resourceLabelOf };
}

/** One block: an entity's findings within one resource, in ranked order. */
type ScopedGroup = { scope: string; entity: GtaEntity | null; findings: GtaFinding[] };

/**
 * The blocks a list of findings is drawn as, resource first and entity second.
 *
 * Two layers rather than one because an entity id only means something inside
 * the resource that produced it: `fxmanifest.lua` names a different file in
 * every pack in the drop. Grouping the resource first is also the order a
 * reader acts in — they open one pack and fix the things wrong with it.
 *
 * Both layers keep ranked order. Resources appear in the order their worst
 * finding does, and so do the entity blocks within a resource — which means
 * undoing one thing `groupByEntity` does deliberately. It trails the
 * entity-less group, on the argument that a missing manifest belongs after the
 * vehicles it would have registered; that is right for a report read top to
 * bottom and wrong for a ranked panel, where a cross-resource error with no
 * single vehicle to name would render below three warnings on an unrelated car
 * while the headline calls it the worst thing on the list. Rank wins here, and
 * the ranking reaching the reader is what this file is for.
 *
 * `"flat"` does none of this: it is the escape hatch for a caller that wants
 * the ranked order unbroken, so the resource is drawn on each row instead.
 */
function scopedGroups(
  findings: readonly GtaFinding[],
  scopeOf: ReadonlyMap<GtaFinding, string>,
  group: "entity" | "flat",
): ScopedGroup[] {
  if (group === "flat") return [{ scope: "", entity: null, findings: [...findings] }];

  const order: string[] = [];
  const byScope = new Map<string, GtaFinding[]>();
  for (const finding of findings) {
    const scope = scopeOf.get(finding) ?? "";
    let held = byScope.get(scope);
    if (!held) {
      held = [];
      byScope.set(scope, held);
      order.push(scope);
    }
    held.push(finding);
  }

  const blocks: ScopedGroup[] = [];
  for (const scope of order) {
    const within = byScope.get(scope) ?? [];
    const rankOf = new Map<GtaFinding, number>();
    within.forEach((finding, index) => {
      if (!rankOf.has(finding)) rankOf.set(finding, index);
    });
    const grouped = groupByEntity(within);
    grouped.sort((a, b) => (rankOf.get(a.findings[0]) ?? 0) - (rankOf.get(b.findings[0]) ?? 0));
    for (const entry of grouped) {
      blocks.push({ scope, entity: entry.entity, findings: entry.findings });
    }
  }
  return blocks;
}

/** What was asked for, so a caller can log or undo it. */
export type FindingsFixRequest = {
  finding: GtaFinding;
  fix: GtaFix;
  /** The file every edit in the batch names. `applyEdits` refuses mixed batches. */
  file: string;
  edits: GtaTextEdit[];
};

/**
 * What happened, in full.
 *
 * `applied` and `rejected` together account for every edit handed in, and both
 * halves travel to the caller. A caller that writes `text` to disk and ignores
 * `rejected` is choosing to hide a refusal, but it cannot do so by accident.
 */
export type FindingsFixResult = FindingsFixRequest & {
  /** The patched text. Identical to the input when every edit was refused. */
  text: string;
  applied: GtaTextEdit[];
  rejected: { edit: GtaTextEdit; reason: string }[];
};

/**
 * The disk, held at arm's length.
 *
 * This component runs in a tab over files it never opened, so a one-click fix
 * has to borrow the text from whoever does hold it. Two functions rather than
 * one because the verify step belongs *here*, between them: if the caller were
 * handed the edits and left to apply them, every tool would need its own copy of
 * the `expect` check and the first one to skip it would write a fix for one
 * resource into another.
 *
 * `readFile` returning `null` is a normal answer, not an error — the tool may
 * have released the upload — and it is reported on screen rather than logged.
 */
export type FindingsFixHandler = {
  /** The current text of `file`, or `null` when the caller no longer holds it. */
  readFile: (file: string) => string | null | Promise<string | null>;
  /** The patched text and every refusal, after the verify step has run. */
  onApplied: (result: FindingsFixResult) => void | Promise<void>;
};

export type FindingsListProps = {
  /**
   * The findings to render. Takes precedence over `report`, so a tool that has
   * already merged, filtered or deduped a set passes the result directly.
   */
  findings?: readonly GtaFinding[];
  /** Read for `skipped`, for the vehicle/resource count, and for findings. */
  report?: FindingsReport;
  /** Whatever the collector could not read, in the collector's own words. */
  truncated?: readonly string[];
  /**
   * `"entity"` gathers findings under the thing they are about; `"flat"` keeps
   * the ranked order unbroken. Grouped is the default because that is the whole
   * argument for `GtaEntity`, and it degrades to a flat list on its own when no
   * finding names an entity.
   */
  group?: "entity" | "flat";
  /** Omit to render no fix buttons at all. */
  fixes?: FindingsFixHandler;
  /** What to say when the checks that ran found nothing. */
  emptyLabel?: string;
  className?: string;
};

// ─── Fix state ───────────────────────────────────────────────────────────────

type FixOutcome =
  | { status: "working" }
  | {
      status: "done";
      applied: number;
      rejected: { edit: GtaTextEdit; reason: string }[];
      /** Set when the caller could not take the patched text back. */
      handoff?: string;
    }
  | { status: "unavailable"; message: string };

/**
 * A fix is offered as one click only at `high` confidence, and only with edits.
 *
 * `GtaFix.confidence` says what `medium` means — right *if the guess about
 * intent is right* — and a button that rewrites a file on a guess is how the
 * Carcols ID Fixer this layer is written against renames kits it cannot find
 * references for. Medium and low fixes still render their label as advice.
 */
function isOneClick(fix: GtaFix | undefined): fix is GtaFix & { edits: GtaTextEdit[] } {
  return fix?.confidence === "high" && Array.isArray(fix.edits) && fix.edits.length > 0;
}

/** A thrown value the user can read, whatever the caller threw. */
function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// ─── Row identity ────────────────────────────────────────────────────────────

/**
 * What makes two findings the same finding, for state that has to outlive a
 * re-render.
 *
 * Not the array they arrived in, and not the index of a row. The array is new
 * on every parent render — `sortFindings`, `dedupeFindings` and `withTag` all
 * return fresh values by design, and `onApplied`'s entire job is to cause a
 * parent render — so state keyed on it is state the fix button destroys a
 * moment after writing it. The index is worse: it moves when the reveal grows
 * or the grouping changes, and an outcome that slides onto a neighbouring row
 * reports a refusal against a file nobody touched.
 *
 * So identity is the finding's content, and it is `findings.ts`'s idea of that
 * content rather than a tuple built here. `findingKey` is the same code, file,
 * line and title join `dedupeFindings` treats as one problem, exported for
 * exactly this. Agreeing with `dedupeFindings` is the point — a caller that
 * deduped and a caller that did not must not disagree about which row a click
 * belongs to — and a private copy of the tuple here would agree right up until
 * one of the two changed.
 *
 * `findingKey` on its own is not enough to key a row, and the reason is the
 * input this layer exists for. Two vendor packs with the same internal layout
 * produce two findings with the same code, file, line and title, differing only
 * in the detail sentence and the entity — one key for two different packs.
 * Telling them apart by which comes first in the ranked list, which is what an
 * appended ordinal does, is a positional index wearing a content key's clothes:
 * fix the first pack, re-scan, and the second pack — untouched, and now first
 * in a shorter list — inherits the first one's key, its "Applied", its disabled
 * button and, where the edit was refused, a refusal against a file nobody has
 * opened. That is the failure the paragraph above rejects index keys to avoid,
 * reintroduced by the tie-break.
 *
 * So the key is widened with the parts of a finding `findingKey` deliberately
 * leaves out — the detail, the entity, and which resource it came out of — and
 * it stays content-derived all the way down. Widening cannot make a deduped
 * caller and an undeduped one disagree about which row a click belongs to,
 * which is what the agreement above is for: the survivor of a dedupe carries
 * the same content, so it carries the same key it had in the undeduped list.
 *
 * One case is left, and it is the one no content key can decide: two findings
 * identical in every field, up to and including the very same object listed
 * twice. Those are told apart by an ordinal, which is enough to draw them as
 * two rows and land a click on one of them — and those keys are named as
 * positional here, so `pruneOutcomes` can refuse to carry one across a change
 * in the list. They are the only keys here that can come to mean a different
 * row, and a "1 edit applied." under a finding nobody clicked is worse than an
 * outcome the panel forgot.
 */
type RowKeys = {
  /** One key per finding, in ranked order. */
  keys: string[];
  /** The keys that are a tie-break, and so mean nothing outside this list. */
  positional: ReadonlySet<string>;
};

/**
 * The parts joined so that no two different tuples can produce one string.
 *
 * Length-prefixed rather than NUL-separated the way `findingKey` joins its own
 * four parts, because what is widened in here includes a finding's `detail` —
 * free prose written by an analyzer — and a length prefix is the version of
 * this that needs no assumption at all about what a part may contain.
 */
function joinParts(parts: readonly string[]): string {
  return parts.map((part) => `${part.length}:${part}`).join("");
}

function rowKeysOf(
  findings: readonly GtaFinding[],
  scopeOf: ReadonlyMap<GtaFinding, string>,
): RowKeys {
  const seen = new Map<string, number>();
  const keys: string[] = [];
  const positional = new Set<string>();

  for (const finding of findings) {
    const identity = joinParts([
      findingKey(finding),
      finding.detail ?? "",
      finding.entity ? `${finding.entity.kind}/${finding.entity.id}` : "",
      scopeOf.get(finding) ?? "",
    ]);
    const occurrence = (seen.get(identity) ?? 0) + 1;
    seen.set(identity, occurrence);
    if (occurrence === 1) {
      keys.push(identity);
      continue;
    }
    const key = joinParts([identity, String(occurrence)]);
    positional.add(key);
    keys.push(key);
  }

  return { keys, positional };
}

/** Whether two key lists describe the same findings in the same order. */
function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

/**
 * The outcomes whose finding is still on the list, and still the same finding.
 *
 * A re-scan drops the findings it fixed, and an outcome for one of those has
 * nothing left to render against. Everything else is kept: a user who applied
 * three fixes and then filtered the list has not stopped having applied them.
 *
 * Except a positional key. Those are the tie-break between findings identical
 * in every field, so the key `…#2` means "the second one of these" and the
 * second one of these is a different row once the list has changed under it.
 * Carrying it over is how an outcome lands on a finding nobody clicked, which
 * is the one thing worse than losing the outcome — so this is called only when
 * the list actually changed, and when it did, the positional keys go.
 *
 * Returns the map unchanged when nothing was dropped, so React can bail out of
 * the update rather than re-rendering to arrive at the same state.
 */
function pruneOutcomes(
  outcomes: Record<string, FixOutcome>,
  keys: RowKeys,
): Record<string, FixOutcome> {
  const held = Object.keys(outcomes);
  if (held.length === 0) return outcomes;
  const live = new Set(keys.keys);
  const survives = (key: string) => live.has(key) && !keys.positional.has(key);
  if (held.every(survives)) return outcomes;
  const kept: Record<string, FixOutcome> = {};
  for (const key of held) {
    if (survives(key)) kept[key] = outcomes[key];
  }
  return kept;
}

// ─── The component ───────────────────────────────────────────────────────────

export function FindingsList({
  findings,
  report,
  truncated,
  group = "entity",
  fixes,
  emptyLabel = "Nothing to fix in the checks that ran.",
  className,
}: FindingsListProps) {
  // Memoized so the sort, the grouping and the row keys below are not recomputed
  // on every render of whatever sits above this. Deliberately *not* what the
  // reveal and the fix outcomes key off: a memo on prop identity is still a new
  // value whenever the caller computes its list inline, which is the documented
  // way to pass one.
  const all = useMemo<readonly GtaFinding[]>(() => {
    if (findings) return findings;
    if (report) return findingsOfReport(report);
    return [];
  }, [findings, report]);

  const ranked = useMemo(() => sortFindings(all), [all]);

  // Which resource each finding came out of, and how each resource is spelled.
  // Read off the report rather than off the findings, because a `GtaFinding`
  // does not carry its resource and the report is the last thing that knows.
  const attribution = useMemo(() => attributionOf(report), [report]);

  /**
   * Every group, whatever the fold drew.
   *
   * This is not what gets rendered — see the fold below — it is where a
   * heading's count and its emergency badge come from, both of which describe
   * the whole entity rather than the rows that fitted.
   */
  const groups = useMemo(
    () => scopedGroups(ranked, attribution.scopeOf, group),
    [ranked, attribution, group],
  );
  const wholeGroupOf = useMemo(() => {
    // Keyed on the scope and then on the entity object rather than on a
    // re-derived string, because `groupByEntity` hands back the entity of the
    // *first* finding it saw for that key, and the visible slice is a prefix of
    // the same ranked list — so the same first finding, and the same object.
    // Re-deriving the key here would mean a second copy of `groupByEntity`'s
    // case-folding rule, which is the copy that eventually disagrees with it.
    const byScope = new Map<string, Map<GtaEntity | null, readonly GtaFinding[]>>();
    for (const entry of groups) {
      let byEntity = byScope.get(entry.scope);
      if (!byEntity) {
        byEntity = new Map();
        byScope.set(entry.scope, byEntity);
      }
      byEntity.set(entry.entity, entry.findings);
    }
    return byScope;
  }, [groups]);

  // One key per row, in ranked order, so `outcomes` can be keyed on what a
  // finding is rather than on where it currently sits or which array it came in.
  const rowKeys = useMemo(() => rowKeysOf(ranked, attribution.scopeOf), [ranked, attribution]);

  const [shown, setShown] = useState(VISIBLE_FINDINGS);
  const [outcomes, setOutcomes] = useState<Record<string, FixOutcome>>({});
  const [rendered, setRendered] = useState<readonly string[]>(rowKeys.keys);

  // What resets the reveal and the fix outcomes is the findings changing — not
  // the caller handing over a new array of the same ones. Those are different
  // events and only the first is a re-scan: `onApplied` is a callback whose job
  // is to store the patched text, and storing it re-renders the caller, which
  // recomputes `sortFindings(dedupeFindings(raw))` into a new array. Resetting
  // on the reference would mean every fix erased its own refusal and every
  // unrelated parent render collapsed a 108-row reveal back to eight.
  //
  // When the set really has changed, an outcome is dropped only if its finding
  // is gone. A user who applied three fixes and re-ran the check on a folder
  // where two problems remain has not stopped having applied the third.
  if (rendered !== rowKeys.keys) {
    const changed = !sameKeys(rendered, rowKeys.keys);
    setRendered(rowKeys.keys);
    if (changed) {
      setShown(VISIBLE_FINDINGS);
      setOutcomes((current) => pruneOutcomes(current, rowKeys));
    }
  }

  const runFix = useCallback(
    async (key: string, request: FindingsFixRequest) => {
      if (!fixes) return;
      setOutcomes((current) => ({ ...current, [key]: { status: "working" } }));

      let text: string | null;
      try {
        text = await fixes.readFile(request.file);
      } catch (error) {
        setOutcomes((current) => ({
          ...current,
          [key]: {
            status: "unavailable",
            message: `The fix could not be applied: ${messageOf(error)}. Nothing was changed.`,
          },
        }));
        return;
      }

      if (typeof text !== "string") {
        setOutcomes((current) => ({
          ...current,
          [key]: {
            status: "unavailable",
            message:
              `The current text of ${request.file} was not available, so nothing was ` +
              "changed. Re-select the folder and try again.",
          },
        }));
        return;
      }

      // The verify step. `expect` is checked against the file as it stands now,
      // which is why an edit calculated before an unrelated save is refused here
      // rather than applied against text it was never read from.
      const result = applyEdits(text, request.edits);
      setOutcomes((current) => ({
        ...current,
        [key]: { status: "done", applied: result.applied.length, rejected: result.rejected },
      }));

      try {
        await fixes.onApplied({ ...request, ...result });
      } catch (error) {
        // Recorded beside the refusals rather than replacing them. A caller that
        // fails to store the patched text is one more thing the user needs to
        // know, not a reason to hide what `applyEdits` already decided.
        setOutcomes((current) => ({
          ...current,
          [key]: {
            status: "done",
            applied: result.applied.length,
            rejected: result.rejected,
            handoff: `The patched text could not be handed back: ${messageOf(error)}.`,
          },
        }));
      }
    },
    [fixes],
  );

  const counts = summarizeFindings(ranked);
  const verdict = verdictFor(ranked);

  // The findings this build could not count. `summarizeFindings` and
  // `verdictFor` both test for the three severities this build defines and fall
  // through for anything else — a finding from an older desktop build, which
  // `findings.ts` says in as many words is a real path — and such a finding is
  // drawn as a row while adding nothing to any number above it. One of them is
  // enough to put "Ready to export" and "0 errors, 0 warnings, 0 notes" over a
  // problem the panel is displaying, which is the green tick this whole layer
  // exists to prevent. So the remainder is named below and the verdict is not
  // allowed to read clean while it is non-zero.
  const unrecognised = ranked.length - (counts.error + counts.warning + counts.info);
  const uncounted = verdict === "clean" && unrecognised > 0;

  const HeadlineIcon =
    verdict === "blocked"
      ? AlertCircle
      : verdict === "warnings" || uncounted
        ? AlertTriangle
        : CheckCircle2;
  const headlineClass =
    verdict === "blocked"
      ? "text-destructive"
      : verdict === "warnings" || uncounted
        ? "text-warning"
        : "text-success";
  const headline = uncounted
    ? `${unrecognised.toLocaleString()} finding${unrecognised === 1 ? "" : "s"} this build cannot rank`
    : describeVerdict(ranked);

  // Derived, never hard-coded: `findings.ts` owns the 20,000 cap and does not
  // export it, and a second copy of the number here is a number that can drift.
  const droppedByCap = all.length - ranked.length;

  // ── The fold ───────────────────────────────────────────────────────────────
  //
  // The rows are chosen first and grouped second, and that order is the whole
  // argument. Grouping first and then filling the row budget group by group
  // spends it on whatever the top group happens to hold: four vehicles carrying
  // three errors and six warnings each draw the first vehicle's three errors
  // and then five of its warnings, while nine errors on the other three
  // vehicles sit below "Show more" — under a headline that honestly reads
  // "12 errors". Five warnings would outrank nine errors on the screen while
  // `sortFindings` says the opposite, which is this file's founding defect one
  // step further down: the ranking discarded on the way to the reader.
  //
  // So the drawn rows are the top `shown` of the ranked list, full stop, and
  // grouping is only the layout applied to them. Grouping a prefix is safe
  // because groups keep first-appearance order: the prefix produces the same
  // blocks in the same order as the whole list would, each of them shorter.
  const visible = ranked.slice(0, shown);

  // Each drawn row takes its own key from the ranked list, by position and not
  // by object identity. `rowKeysOf` already gave two rows carrying the very
  // same finding object two different keys; a lookup keyed on the object throws
  // that away again, and React then sees one key on two children — which it
  // warns about, and which makes one click's "Applied" appear under both rows.
  // A queue per object rather than an index, because the rows are laid out by
  // group and no longer arrive in ranked order.
  const keyQueue = new Map<GtaFinding, string[]>();
  visible.forEach((finding, index) => {
    const held = keyQueue.get(finding);
    if (held) held.push(rowKeys.keys[index]);
    else keyQueue.set(finding, [rowKeys.keys[index]]);
  });

  const blocks = scopedGroups(visible, attribution.scopeOf, group).map((entry) => {
    // The count and the badge describe the whole entity, never the drawn rows:
    // a vehicle does not stop being emergency fleet, or stop having twenty
    // problems, because the fold cut it short at eight.
    const whole = wholeGroupOf.get(entry.scope)?.get(entry.entity) ?? entry.findings;
    // A `resource`-kind entity id is the drop-relative root `analyze.ts` puts
    // there to keep two packs apart; it is not how any other sentence in the
    // report spells that pack, so it is resolved back to the resource's label.
    const entityLabel = entry.entity
      ? entry.entity.kind === "resource"
        ? (attribution.resourceLabelOf.get(normalizeName(entry.entity.id)) ?? entry.entity.id)
        : entry.entity.id
      : "";
    return {
      key: `${entry.scope} ${entry.entity ? `${entry.entity.kind} ${entry.entity.id}` : "unattached"}`,
      entity: entry.entity,
      entityLabel,
      /** Which pack this block is about, where there is more than one. */
      resource: attribution.labelOf.get(entry.scope) ?? "",
      rows: entry.findings.map((finding) => ({
        finding,
        // Falls back rather than throwing: an unkeyed row still renders, it
        // just shares an outcome with its twin, which is a worse row and not a
        // blank panel.
        key: keyQueue.get(finding)?.shift() ?? findingKey(finding),
        resource: attribution.labelOf.get(attribution.scopeOf.get(finding) ?? "") ?? "",
      })),
      /** The group's true size, which is not `rows.length` once the fold bites. */
      total: whole.length,
      // Only asked where there is a heading to carry the badge. An entity-less
      // group badges its own rows instead, and `whole` there is every
      // unattached finding in the report — a scan whose answer is never read.
      emergency: entry.entity !== null && whole.some(isEmergencyFinding),
    };
  });
  const drawn = visible.length;
  const hidden = ranked.length - drawn;

  const skipped = report?.skipped ?? [];

  return (
    <div className={cn("space-y-2", className)} data-testid="gta-findings">
      <div className="flex flex-wrap items-center gap-2">
        <HeadlineIcon className={cn("h-4 w-4 shrink-0", headlineClass)} />
        <span className="text-sm font-medium">{headline}</span>
        {/*
          Only where there is a `report`. `toMarkdown` exports the artifact the
          analysis produced — its skipped list, its resource attribution, the
          game build its vanilla rules ran against — and a caller that passed
          `findings` alone has none of that to export. A button that copied a
          bare list under a heading claiming to be a report would be the same
          quiet half-answer this whole file is written against, so it is absent
          rather than degraded.
        */}
        {report && <CopyMarkdown report={report} className="ml-auto" />}
      </div>

      <p className="text-xs text-muted-foreground">
        {counts.error} error{counts.error === 1 ? "" : "s"}, {counts.warning} warning
        {counts.warning === 1 ? "" : "s"}, {counts.info} note{counts.info === 1 ? "" : "s"}
        {describeScope(report)}.
      </p>

      {/* The rows this build drew but could not count. Said in the same register
          as the cap notice below, because it is the same kind of statement: a
          number above the list does not describe everything under it. */}
      {unrecognised > 0 && (
        <p className="text-xs text-warning">
          {unrecognised.toLocaleString()} further finding
          {unrecognised === 1 ? " carries a severity" : "s carry a severity"} this build does not
          define, so {unrecognised === 1 ? "it is" : "they are"} shown below but counted in none of
          the numbers above.
        </p>
      )}

      {ranked.length === 0 && <p className="text-xs text-muted-foreground">{emptyLabel}</p>}

      {blocks.map((block) => (
        <FindingGroup
          key={block.key}
          entity={block.entity}
          entityLabel={block.entityLabel}
          resource={block.resource}
          rows={block.rows}
          total={block.total}
          emergency={block.emergency}
          fixes={fixes}
          outcomes={outcomes}
          onApply={runFix}
        />
      ))}

      {hidden > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded border border-border px-2 py-1 text-xs font-medium hover:bg-muted"
            onClick={() => setShown((current) => current + FINDINGS_PAGE)}
          >
            Show {Math.min(hidden, FINDINGS_PAGE).toLocaleString()} more
          </button>
          {/* Counts the whole ranked set, never the drawn slice. A list that
              says "8 findings" over a folder with four hundred is the exact
              dishonesty this layer is written against. */}
          <span className="text-xs text-muted-foreground">
            Showing {drawn.toLocaleString()} of {ranked.length.toLocaleString()} findings.
          </span>
        </div>
      )}

      {droppedByCap > 0 && (
        <p className="text-xs text-warning">
          {droppedByCap.toLocaleString()} further finding{droppedByCap === 1 ? " was" : "s were"}{" "}
          produced and {droppedByCap === 1 ? "is" : "are"} not counted above:{" "}
          {ranked.length.toLocaleString()} is the most this report will rank. Fix what is here and
          re-run the check.
        </p>
      )}

      {skipped.length > 0 && (
        <div className="border-t border-border pt-2 text-xs text-muted-foreground">
          {/* `analyze.ts` fills this in whenever a rule could not run. A green
              tick over a report that skipped half its rules is the failure that
              module's header names, and dropping the field here recreates it. */}
          <span className="font-medium">Not checked: </span>
          {skipped.join(" ")}
        </div>
      )}

      {truncated && truncated.length > 0 && (
        <div className="text-xs text-muted-foreground">{truncated.join(" ")}</div>
      )}
    </div>
  );
}

/**
 * What the numbers were counted over: "across 12 resources", "across 3
 * vehicles", "across 40 stream assets", or nothing at all.
 *
 * Nothing at all is reserved for the cases where there is genuinely no scope to
 * name — no report, or a report over an empty drop — and never used to hide one.
 * The reason there are three scopes rather than one is the liveries pack: a
 * resource with no `vehicles.meta` has no vehicles, and "across 0 vehicles" on
 * the only line that says what was examined reads as "nothing was checked" for
 * a pack whose forty stream assets were cross-referenced against every other
 * resource in the drop. So a resource that has no vehicles is described by what
 * it does have, and a resource that has neither says nothing rather than saying
 * zero.
 */
function describeScope(report: FindingsReport | undefined): string {
  if (!report) return "";
  if ("resources" in report) {
    const count = report.resources.length;
    return count === 0 ? "" : ` across ${count} resource${count === 1 ? "" : "s"}`;
  }
  const vehicles = report.vehicles.length;
  if (vehicles > 0) return ` across ${vehicles} vehicle${vehicles === 1 ? "" : "s"}`;
  const assets = report.stream.length;
  if (assets > 0) return ` across ${assets} stream asset${assets === 1 ? "" : "s"}`;
  return "";
}

// ─── Getting the report out of the tab ───────────────────────────────────────

/**
 * The one thing a user actually wants to do with a conflict report.
 *
 * A finding is only worth producing if it can reach the person who can act on
 * it, and that person is almost never the person running the scan — it is
 * whoever built the pack. Until this button, every report this layer produced
 * was a React tree: close the tab and the scan is gone, with nothing to paste
 * into the thread where the pack is being discussed and nothing to compare a
 * second scan against. That is the single defect all twenty-four of the
 * competitor's free tools share, and their own documentation tells users to
 * rescan and compare like-for-like — advice their tools make impossible.
 *
 * It lives here rather than in each of the eight tools because a copy button
 * that eight surfaces each wrote is eight chances for one of them to export
 * something that disagrees with the panel above it. `toMarkdown` already carries
 * the skipped list and the vanilla index's game build into the paste, so what a
 * reader gets months later says what was checked and what was not.
 *
 * `toMarkdown` does not throw by contract — it is written for exactly this call
 * site, where an exception costs the user the report they were trying to save.
 * The clipboard is the part that can fail, and a denied clipboard looks exactly
 * like a working one unless it says so, after which somebody pastes whatever
 * they copied ten minutes ago into a pull request about somebody else's pack.
 * So a refusal is drawn, not swallowed.
 */
function CopyMarkdown({ report, className }: { report: FindingsReport; className?: string }) {
  const [state, setState] = useState<"idle" | "done" | "blocked">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = window.setTimeout(() => setState("idle"), 1800);
    return () => window.clearTimeout(timer);
  }, [state]);

  return (
    <button
      type="button"
      className={cn(
        "flex items-center gap-1.5 rounded border border-border px-2 py-1 text-xs font-medium hover:bg-muted",
        className,
      )}
      aria-label={
        state === "blocked"
          ? "Clipboard blocked — select the report by hand"
          : state === "done"
            ? "Report copied as Markdown"
            : "Copy the report as Markdown"
      }
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(toMarkdown(report));
          setState("done");
        } catch {
          setState("blocked");
        }
      }}
    >
      {state === "done" ? (
        <Check className="h-3.5 w-3.5" />
      ) : state === "blocked" ? (
        <AlertTriangle className="h-3.5 w-3.5" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
      {state === "done" ? "Copied" : state === "blocked" ? "Clipboard blocked" : "Copy Markdown"}
    </button>
  );
}

// ─── One entity's block ──────────────────────────────────────────────────────

function FindingGroup({
  entity,
  entityLabel,
  resource,
  rows,
  total,
  emergency,
  fixes,
  outcomes,
  onApply,
}: {
  entity: GtaEntity | null;
  /**
   * How the entity is spelled, which is `entity.id` for every kind except
   * `resource` — see `attributionOf`. Decided by the caller because only it can
   * see the whole set the spelling is disambiguated against.
   */
  entityLabel: string;
  /**
   * Which pack these findings came out of, or `""` for a single-resource report
   * and for findings that belong to the set rather than to one of its members.
   * Drawn on the heading, because two packs in one drop can hold the same
   * entity id and a heading that did not say which pack is a heading a reader
   * cannot act on.
   */
  resource: string;
  /**
   * The rows the fold left room for, each carrying the key its outcome is
   * filed under. The key is handed down rather than derived here, because it
   * has to mean the same thing across a re-render that changed the grouping,
   * and only the caller can see the whole ranked list it is computed over.
   *
   * `resource` on a row is only drawn where the heading is not already carrying
   * it — a flat list has no heading — so a pack is named exactly once per row.
   */
  rows: { finding: GtaFinding; key: string; resource: string }[];
  /** Everything this entity has wrong with it, drawn or not. */
  total: number;
  /**
   * True when any finding in the whole group is emergency fleet — decided by the
   * caller over the group, because a vehicle does not stop being a police car
   * when the fold cuts its rows short. The badge sits here rather than on every
   * row so that one police car with eight consequences shows one siren; a
   * finding that names no entity has no heading to carry it and badges its row.
   */
  emergency: boolean;
  fixes: FindingsFixHandler | undefined;
  outcomes: Record<string, FixOutcome>;
  onApply: (key: string, request: FindingsFixRequest) => void;
}) {
  // A heading for a block that has something to say about itself: an entity, a
  // pack, or both. A block with neither is the ordinary single-resource flat
  // list, which has never had one.
  const heading = entity !== null || resource !== "";
  // Not repeated where the entity *is* the resource — "Resource police (from
  // vendor-a.zip)" already says which pack, and saying it twice reads as two.
  const showResource = resource !== "" && resource !== entityLabel;

  return (
    <div className="space-y-1" data-testid="gta-findings-group">
      {heading && (
        <p
          className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium text-muted-foreground"
          data-testid="gta-findings-heading"
        >
          {entity && (
            <>
              <span className="uppercase tracking-wide">
                {ENTITY_LABEL[entity.kind] ?? entity.kind}
              </span>
              <span className="font-mono text-foreground">{entityLabel}</span>
            </>
          )}
          {showResource && <span>{entity ? `in ${resource}` : `In ${resource}`}</span>}
          {/* The group's own count is the group's whole size. Printing the
              drawn slice here would understate one vehicle in exactly the way
              the total above the list must never understate the report. */}
          <span>
            · {rows.length < total ? `${rows.length} of ${total}` : total} finding
            {total === 1 ? "" : "s"}
          </span>
          {emergency && <EmergencyBadge />}
        </p>
      )}
      <ul className="space-y-2">
        {rows.map((row) => (
          <FindingRow
            key={row.key}
            finding={row.finding}
            rowKey={row.key}
            resource={heading ? "" : row.resource}
            showEmergency={!entity}
            fixes={fixes}
            outcome={outcomes[row.key]}
            onApply={onApply}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * The one piece of ranking made by somebody who has run a server.
 *
 * Drawn from `isEmergencyFinding` rather than from an inline tag check, because
 * `sortFindings` lifts a finding within its severity band on exactly the same
 * predicate: reading the tag twice is how the badge and the order come to
 * disagree about which finding is the police car. A siren that does not light up
 * is noticed by players within minutes; a misspelled `handlingId` on a tuner car
 * can go unreported for weeks.
 */
function EmergencyBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 align-middle text-[10px] font-normal text-warning"
      data-testid="gta-findings-emergency"
    >
      <Siren className="h-3 w-3" />
      emergency vehicle
    </span>
  );
}

function FindingRow({
  finding,
  rowKey,
  resource,
  showEmergency,
  fixes,
  outcome,
  onApply,
}: {
  finding: GtaFinding;
  rowKey: string;
  /**
   * Which pack this finding came out of, where no heading above the row is
   * already saying so. `""` for every single-resource caller, which is why
   * PackBench's panel is unchanged by any of this.
   */
  resource: string;
  showEmergency: boolean;
  fixes: FindingsFixHandler | undefined;
  outcome: FixOutcome | undefined;
  onApply: (key: string, request: FindingsFixRequest) => void;
}) {
  const Icon = SEVERITY_ICON[finding.severity] ?? Info;
  const severityClass = SEVERITY_CLASS[finding.severity] ?? UNKNOWN_SEVERITY_CLASS;
  const fix = finding.fix;
  // Narrowed once: a fix is clickable only when it is `high` confidence, carries
  // edits, and there is somebody able to read the file back.
  const clickable = fix !== undefined && fixes !== undefined && isOneClick(fix) ? fix : null;
  const applied = outcome?.status === "done" && outcome.applied > 0;

  return (
    <li className="flex gap-2" data-testid="gta-finding">
      <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", severityClass)} />
      <div className="min-w-0 space-y-0.5">
        <p className="select-text text-xs font-medium leading-snug">
          {finding.title}
          {showEmergency && isEmergencyFinding(finding) && (
            <>
              {" "}
              <EmergencyBadge />
            </>
          )}
        </p>
        <p className="select-text text-[11px] leading-snug text-muted-foreground">
          {finding.detail}
        </p>
        {(finding.file || resource) && (
          <p className="font-mono text-[10px] text-muted-foreground/70">
            {finding.file}
            {finding.file && finding.line ? `:${finding.line}` : ""}
            {/* The path is resource-relative, so `vehicles.meta` names a
                different file in every pack in the drop. Naming the pack beside
                it is what makes the row point at a file somebody can open. */}
            {resource && `${finding.file ? " — " : ""}in ${resource}`}
          </p>
        )}

        {fix && !clickable && (
          // Medium and low confidence: the label is advice, and the user does
          // the edit. Offering a button here is what turns a guess about intent
          // into a rewrite nobody asked for.
          <p className="text-[11px] leading-snug text-muted-foreground">Suggested: {fix.label}</p>
        )}

        {clickable && (
          <button
            type="button"
            disabled={outcome?.status === "working" || applied}
            className="mt-1 inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] font-medium hover:bg-muted disabled:opacity-60"
            data-testid="gta-finding-fix"
            onClick={() =>
              onApply(rowKey, {
                finding,
                fix: clickable,
                // Every edit in a batch names the same file — `applyEdits`
                // refuses a mixed batch outright rather than guessing which
                // half belongs to the text in hand.
                file: clickable.edits[0].file,
                edits: clickable.edits,
              })
            }
          >
            <Wrench className="h-3 w-3" />
            {applied ? "Applied" : outcome?.status === "working" ? "Applying..." : clickable.label}
          </button>
        )}

        {outcome?.status === "unavailable" && (
          <p className="text-[11px] leading-snug text-destructive">{outcome.message}</p>
        )}

        {outcome?.status === "done" && (
          <div className="space-y-0.5">
            {outcome.applied > 0 && (
              <p className="text-[11px] leading-snug text-success">
                {outcome.applied} edit{outcome.applied === 1 ? "" : "s"} applied.
              </p>
            )}
            {/* Every refusal, in the words `applyEdits` used. The `expect` field
                exists so an edit can be refused with a reason; a UI that showed
                only the successes would make that mechanism invisible and leave
                the user believing the fix landed. */}
            {outcome.rejected.map((rejection, index) => (
              <p
                key={`${rejection.edit.start}-${index}`}
                className="text-[11px] leading-snug text-destructive"
                data-testid="gta-finding-rejection"
              >
                Refused: {rejection.reason}
              </p>
            ))}
            {outcome.applied === 0 && outcome.rejected.length > 0 && (
              <p className="text-[11px] leading-snug text-muted-foreground">
                Nothing was written to {clickable ? clickable.edits[0].file : "the file"}.
              </p>
            )}
            {outcome.handoff && (
              <p className="text-[11px] leading-snug text-destructive">{outcome.handoff}</p>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
